import { FC, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ComplianceWithCoursesResponseHydrated as ComplianceWithCourses } from "src/api/compliance";

import ExpandableSection from "../../../components/ExpandableSection";
import ExpandCollapseButton from "../../../components/ExpandCollapseButton";
import CourseStatusIcon from "../../../components/CourseStatusIcon";
import { useExpandableState } from "../../../hooks/useExpandableState";
import { formatDate } from "../../../services/formatters";
import PositionDetailView from "../shared/PositionDetailView";
import ProfileSetupWarning from "../ProfileSetupWarning";
import type { ProfileSetupHints } from "../profileSetup";

interface PositionTitleProps {
  position: {
    id: number;
    name: string;
    status: string | null;
    validUntil: string | null;
  };
}

const PositionTitle: FC<PositionTitleProps> = ({ position }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <CourseStatusIcon status={position.status} />
      <Typography variant={"body1"} style={{ marginLeft: ".5rem" }}>
        {position.name}
      </Typography>
    </Box>
    {position.validUntil && (
      <Typography variant="body2" style={{ color: "#666", fontSize: "0.9em", marginLeft: "1rem" }}>
        {formatDate(position.validUntil)}
      </Typography>
    )}
  </Box>
);

interface PositionsProps {
  complianceData: ComplianceWithCourses;
  userId?: number;
  crewId?: number;
  profileSetup?: ProfileSetupHints;
}

const Positions: FC<PositionsProps> = ({ complianceData, userId, crewId, profileSetup }) => {
  const { t } = useTranslation();

  const incompleteRoleById = new Map((profileSetup?.incompleteRoles ?? []).map((entry) => [entry.roleId, entry]));
  const roles = complianceData?.relationTree?.roles;

  // A role is shown when it has assigned positions, courses attached directly
  // to it, or an action-required warning (CREW-530). Roles with none of these
  // (e.g. Administrator: nothing required, no courses) stay hidden.
  const visibleRoles = (roles ?? []).filter(
    (role) => role.positions.length > 0 || role.courseIds.length > 0 || incompleteRoleById.has(role.id),
  );

  // Get all expandable IDs for expand/collapse. Hint-only roles (no positions,
  // no direct courses) render a heading + hint without an expandable section.
  const allPositionIds = useMemo(() => {
    const ids: string[] = [];
    visibleRoles.forEach((role) => {
      if (role.positions.length === 0) {
        if (role.courseIds.length > 0) {
          ids.push(`role-as-position-${role.id}`);
        }
      } else {
        role.positions.forEach((position) => {
          ids.push(`position-${position.id}`);
        });
      }
    });
    return ids;
  }, [visibleRoles]);

  const storageKey = `expand./crew/${crewId}/members/${userId}/compliance/positions`;
  const expandableState = useExpandableState({
    storageKey,
    defaultExpanded: false,
  });

  const handleToggleAll = () => {
    if (expandableState.hasAnyExpanded(allPositionIds)) {
      expandableState.collapseAll(allPositionIds);
    } else {
      expandableState.expandAll(allPositionIds);
    }
  };

  // Scroll to position if hash is present in URL
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove the '#'
    if (hash && allPositionIds.includes(hash)) {
      // Expand the section first
      expandableState.setExpanded(hash, true);

      // Scroll to it after a short delay to let it expand
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [allPositionIds, expandableState]);

  if (!roles) {
    return <Typography>{t("compliance.no_position_data")}</Typography>;
  }

  const { certificateMap } = complianceData;

  return (
    <>
      {visibleRoles.length === 0 && <Typography>{t("compliance.no_positions_assigned")}</Typography>}
      {visibleRoles.map((role, roleIndex) => (
        <Box
          key={`role-${role.id}`}
          sx={{
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h5">{role.name}</Typography>
            {roleIndex === 0 && (
              <ExpandCollapseButton
                expanded={expandableState.hasAnyExpanded(allPositionIds)}
                onToggle={handleToggleAll}
                type="positions"
              />
            )}
          </Box>
          {(() => {
            const incomplete = incompleteRoleById.get(role.id);
            if (!incomplete || !profileSetup) return null;

            return (
              <ProfileSetupWarning
                incomplete={incomplete}
                isSelfView={profileSetup.isSelfView}
                onEditProperties={profileSetup.onEditProperties}
              />
            );
          })()}
          {role.positions.length === 0
            ? role.courseIds.length > 0 &&
              (() => {
                const rowId = `role-as-position-${role.id}`;
                return (
                  <ExpandableSection
                    key={rowId}
                    title={
                      <PositionTitle
                        position={{ id: role.id, name: role.name, status: role.status, validUntil: role.validUntil }}
                      />
                    }
                    navId={rowId}
                    expanded={expandableState.isExpanded(rowId)}
                    onChange={(expanded) => expandableState.setExpanded(rowId, expanded)}
                  >
                    <Box
                      sx={{
                        ml: 2,
                      }}
                    >
                      <PositionDetailView
                        position={{
                          id: role.id,
                          name: role.name,
                          status: role.status,
                          validUntil: role.validUntil,
                          courseIds: role.courseIds,
                        }}
                        certificateMap={certificateMap}
                      />
                    </Box>
                  </ExpandableSection>
                );
              })()
            : role.positions.map((position) => {
                const positionId = `position-${position.id}`;
                return (
                  <ExpandableSection
                    key={positionId}
                    title={<PositionTitle position={position} />}
                    navId={positionId}
                    expanded={expandableState.isExpanded(positionId)}
                    onChange={(expanded) => expandableState.setExpanded(positionId, expanded)}
                  >
                    <Box
                      sx={{
                        ml: 2,
                      }}
                    >
                      <PositionDetailView
                        position={{
                          id: position.id,
                          name: position.name,
                          status: position.status,
                          validUntil: position.validUntil,
                          courseIds: position.courseIds,
                        }}
                        certificateMap={certificateMap}
                      />
                    </Box>
                  </ExpandableSection>
                );
              })}
        </Box>
      ))}
    </>
  );
};

export default Positions;
